// MockBridge: the real contract (same printer wrapper, same timeout, same queue) with a
// scriptable device behind it. Used in unit tests, e2e tests and browser development.

import { parseScan } from '@enp/domain';
import { createPrinter, type PrinterTransport, type TransportOutcome } from './printer.js';
import { ReprintQueue } from './reprint-queue.js';
import { delay } from './timeout.js';
import {
  DEVICE_TIMEOUT_MS,
  type ScanError,
  type Device,
  type DeviceBridge,
  type LabelJob,
  type PrintFailureReason,
  type Printer,
  type ScanOptions,
  type ScanRead,
  type Scanner,
} from './types.js';

/** How the mock printer behaves for the next job(s). */
export type MockPrintBehaviour =
  | 'ok'
  /** Never answers: the 5-second timeout decides. */
  | 'hang'
  | Exclude<PrintFailureReason, 'timeout'>;

export interface MockScan {
  text: string;
  format?: string;
  /** Wait before this read (default 0). */
  delayMs?: number;
}

export interface MockBridgeOptions {
  /** Printer behaviour; a function decides per job. Default `'ok'`. */
  print?: MockPrintBehaviour | ((job: LabelJob) => MockPrintBehaviour);
  /** Simulated time a successful print takes (default 0). */
  printDelayMs?: number;
  /** Scripted scan results, consumed in order. */
  scans?: (string | MockScan)[];
  /** Makes every scan fail with this error. */
  scanError?: ScanError;
  devices?: Device[];
  timeoutMs?: number;
  now?: () => Date;
}

export class MockBridge implements DeviceBridge {
  readonly kind = 'mock' as const;
  readonly reprints = new ReprintQueue();
  readonly printer: Printer;
  readonly scanner: Scanner;
  /** Every job the mock printer accepted, in order. */
  readonly printed: LabelJob[] = [];
  /** Every job it was asked to print, including failures. */
  readonly attempts: LabelJob[] = [];

  #options: MockBridgeOptions;
  #scans: MockScan[];

  constructor(options: MockBridgeOptions = {}) {
    this.#options = options;
    this.#scans = (options.scans ?? []).map((s) => (typeof s === 'string' ? { text: s } : s));

    const transport: PrinterTransport = {
      kind: 'mock',
      send: (job, signal) => this.#send(job, signal),
      probe: async () => {
        const behaviour = this.#behaviour();
        if (behaviour === 'ok')
          return { state: 'ready', target: 'mock', message: 'Mock printer ready' };
        if (behaviour === 'hang') return new Promise(() => undefined);
        if (behaviour === 'not-configured') return { state: 'not-configured' };
        if (behaviour === 'unsupported') return { state: 'unsupported' };
        return { state: 'offline', target: 'mock' };
      },
    };
    this.printer = createPrinter({
      transport,
      queue: this.reprints,
      timeoutMs: options.timeoutMs ?? DEVICE_TIMEOUT_MS,
      now: options.now,
    });
    this.scanner = { scan: (opts) => this.#scan(opts) };
  }

  /** Changes printer behaviour from now on (e.g. to test a reprint after a failure). */
  setPrintBehaviour(print: MockBridgeOptions['print']): void {
    this.#options = { ...this.#options, print };
  }

  /** Adds scripted scan results. */
  queueScans(...scans: (string | MockScan)[]): void {
    this.#scans.push(...scans.map((s) => (typeof s === 'string' ? { text: s } : s)));
  }

  async discover(): Promise<Device[]> {
    return this.#options.devices ?? [];
  }

  #behaviour(job?: LabelJob): MockPrintBehaviour {
    const { print = 'ok' } = this.#options;
    return typeof print === 'function' ? (job ? print(job) : 'ok') : print;
  }

  async #send(job: LabelJob, signal: AbortSignal): Promise<TransportOutcome> {
    this.attempts.push(job);
    const behaviour = this.#behaviour(job);
    if (behaviour === 'hang') {
      await new Promise((resolve) => signal.addEventListener('abort', resolve, { once: true }));
      return { ok: false, reason: 'timeout' };
    }
    if (behaviour !== 'ok') return { ok: false, reason: behaviour };
    if (this.#options.printDelayMs) await delay(this.#options.printDelayMs, signal);
    this.printed.push(job);
    return { ok: true };
  }

  async *#scan(opts: ScanOptions = {}): AsyncGenerator<ScanRead> {
    const { signal, once = true } = opts;
    if (this.#options.scanError) throw this.#options.scanError;
    while (!signal?.aborted) {
      const next = this.#scans.shift();
      if (!next) {
        // Nothing scripted: wait like a camera pointed at nothing, until aborted.
        if (!signal) return;
        await new Promise((resolve) => signal.addEventListener('abort', resolve, { once: true }));
        return;
      }
      if (next.delayMs) {
        try {
          await delay(next.delayMs, signal);
        } catch {
          return;
        }
      }
      yield {
        text: next.text,
        format: next.format ?? 'QRCode',
        parsed: parseScan(next.text),
        source: 'mock',
      };
      if (once) return;
    }
  }
}
